import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Layout, 
  Play, 
  Save, 
  Download, 
  RefreshCw, 
  Settings, 
  Monitor, 
  Smartphone, 
  Tablet,
  Code2,
  FileJson,
  FileType2,
  Braces,
  Terminal,
  Maximize2,
  Minimize2,
  Copy,
  Check,
  AlertCircle,
  Moon,
  Sun,
  Undo2,
  Redo2,
  Search,
  Replace
} from 'lucide-react';

// ============================================
// FREDITOR - TƏKMİLLƏŞDİRİLMİŞ EDITOR
// ============================================

const Freditor = ({ 
  initialHtml = '',
  initialCss = '',
  initialJs = '',
  onSave,
  height = '100vh',
  readOnly = false
}) => {
  // Default starter code
  const defaultHtml = `<div class="container">
  <div class="card">
    <h1>🚀 Freditor</h1>
    <p>HTML, CSS, JavaScript & SCSS dəstəklənir!</p>
    <button id="btn" class="btn-primary">Mənə kliklə</button>
    <div id="output"></div>
  </div>
</div>`;

  const defaultCss = `/* CSS Variables */
:root {
  --primary: #667eea;
  --secondary: #764ba2;
  --bg: #f7fafc;
  --text: #2d3748;
}

/* SCSS features: nesting, variables */
.container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  font-family: 'Segoe UI', sans-serif;

  .card {
    background: white;
    padding: 2rem;
    border-radius: 1rem;
    box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    text-align: center;
    max-width: 400px;
    
    h1 {
      color: var(--primary);
      margin-bottom: 0.5rem;
    }
    
    p {
      color: var(--text);
      margin-bottom: 1.5rem;
    }
  }
}

.btn-primary {
  background: var(--primary);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--secondary);
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.4);
  }
}

#output {
  margin-top: 1rem;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 0.5rem;
  min-height: 50px;
  
  &.success {
    background: #c6f6d5;
    color: #22543d;
  }
}`;

  const defaultJs = `// Modern JavaScript (ES6+)
const btn = document.getElementById('btn');
const output = document.getElementById('output');

// Event delegation with arrow function
btn.addEventListener('click', () => {
  // Async simulation
  btn.disabled = true;
  btn.textContent = '⏳ Gözləyin...';
  
  setTimeout(() => {
    const messages = [
      '🎉 Freditor mükəmməl işləyir!',
      '✨ ES6+ dəstəklənir',
      '🚀 Async/Await işləyir',
      '💅 SCSS avtomatik compile'
    ];
    
    const randomMsg = messages[Math.floor(Math.random() * messages.length)];
    
    output.innerHTML = \`
      <strong>\${randomMsg}</strong><br>
      <small>Vaxt: \${new Date().toLocaleTimeString('az-AZ')}</small>
    \`;
    output.className = 'success';
    
    btn.disabled = false;
    btn.textContent = 'Təkrar kliklə';
    
    console.log('✅ Button clicked successfully!');
  }, 1000);
});

// Console override for preview
const originalLog = console.log;
console.log = (...args) => {
  originalLog(...args);
  window.parent.postMessage({ type: 'console', data: args.join(' ') }, '*');
};

// Error handling
window.onerror = (msg, url, line) => {
  console.error(\`❌ Xəta (sətir \${line}): \${msg}\`);
  return false;
};`;

  // State
  const [html, setHtml] = useState(initialHtml || defaultHtml);
  const [css, setCss] = useState(initialCss || defaultCss);
  const [js, setJs] = useState(initialJs || defaultJs);
  const [activeTab, setActiveTab] = useState('html');
  const [layout, setLayout] = useState('horizontal'); // horizontal, vertical, preview-only, editor-only
  const [device, setDevice] = useState('desktop'); // desktop, tablet, mobile
  const [darkMode, setDarkMode] = useState(true);
  const [autoRun, setAutoRun] = useState(true);
  const [consoleOutput, setConsoleOutput] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isCompiling, setIsCompiling] = useState(false);
  
  // History for undo/redo
  const [history, setHistory] = useState([{ html: defaultHtml, css: defaultCss, js: defaultJs }]);
  const [historyIndex, setHistoryIndex] = useState(0);

  const iframeRef = useRef(null);
  const textareaRef = useRef(null);
  const consoleEndRef = useRef(null);
  const autoRunTimeoutRef = useRef(null);

  // ============================================
  // SCSS COMPILER - Dəyişdirilmiş (CSS-only)
  // ============================================
  
  const compileScss = useCallback((scssCode) => {
    // Sadə SCSS-to-CSS converter (nesting, variables)
    let css = scssCode;
    
    // 1. SCSS variables to CSS variables
    const variables = {};
    const varRegex = /\$([\w-]+):\s*([^;]+);/g;
    let match;
    while ((match = varRegex.exec(scssCode)) !== null) {
      variables[`$${match[1]}`] = match[2].trim();
    }
    
    // Replace variables
    Object.entries(variables).forEach(([name, value]) => {
      css = css.replace(new RegExp(`\\${name}(?!\\w)`, 'g'), value);
    });
    
    // 2. Nesting düzəlt (sadə)
    css = css.replace(/([^{]+)\{([^}]*)\{([^}]*)\}/g, (match, parent, child, content) => {
      const parentSelector = parent.trim();
      const childSelector = child.replace(/&/g, '').trim();
      return `${parentSelector} { ${child.replace(/{[^}]*}/, '')} }\n${parentSelector} ${childSelector} { ${content} }`;
    });
    
    return css;
  }, []);

  // ============================================
  // OUTPUT GENERATOR
  // ============================================

  const generateOutput = useCallback(() => {
    const compiledCss = compileScss(css);
    
    return `
<!DOCTYPE html>
<html lang="az">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Freditor Preview</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', sans-serif; }
    ${compiledCss}
  </style>
</head>
<body>
  ${html}
  <script>
    (function() {
      // Console override
      const originalLog = console.log;
      const originalError = console.error;
      const originalWarn = console.warn;
      
      const sendToParent = (type, args) => {
        try {
          window.parent.postMessage({
            type: 'console',
            level: type,
            data: args.map(arg => {
              if (typeof arg === 'object') return JSON.stringify(arg, null, 2);
              return String(arg);
            }).join(' '),
            timestamp: new Date().toLocaleTimeString('az-AZ')
          }, '*');
        } catch(e) {}
      };
      
      console.log = (...args) => {
        originalLog(...args);
        sendToParent('log', args);
      };
      
      console.error = (...args) => {
        originalError(...args);
        sendToParent('error', args);
      };
      
      console.warn = (...args) => {
        originalWarn(...args);
        sendToParent('warn', args);
      };
      
      // Error handler
      window.onerror = (msg, url, line, col, error) => {
        sendToParent('error', [\`❌ \${msg} (sətir \${line}:\${col})\`]);
        return false;
      };
      
      window.onunhandledrejection = (event) => {
        sendToParent('error', [\`❌ Unhandled Promise: \${event.reason}\`]);
      };
      
      // Run user code
      try {
        ${js}
      } catch(err) {
        sendToParent('error', [\`❌ Runtime Error: \${err.message}\`]);
      }
    })();
  </script>
</body>
</html>`;
  }, [html, css, js, compileScss]);

  // ============================================
  // IFRAME UPDATE
  // ============================================

  const updatePreview = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    
    setIsCompiling(true);
    
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;
      
      doc.open();
      doc.write(generateOutput());
      doc.close();
      
      setErrors([]);
    } catch (err) {
      setErrors([err.message]);
    } finally {
      setTimeout(() => setIsCompiling(false), 300);
    }
  }, [generateOutput]);

  // Auto-run with debounce
  useEffect(() => {
    if (!autoRun) return;
    
    clearTimeout(autoRunTimeoutRef.current);
    autoRunTimeoutRef.current = setTimeout(() => {
      updatePreview();
    }, 800);
    
    return () => clearTimeout(autoRunTimeoutRef.current);
  }, [html, css, js, autoRun, updatePreview]);

  // Message listener for console
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'console') {
        setConsoleOutput(prev => [...prev.slice(-50), {
          level: event.data.level,
          data: event.data.data,
          timestamp: event.data.timestamp
        }]);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Scroll console to bottom
  useEffect(() => {
    consoleEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [consoleOutput]);

  // Initial load
  useEffect(() => {
    updatePreview();
  }, [updatePreview]);

  // ============================================
  // ACTIONS
  // ============================================

  const handleManualRun = () => {
    updatePreview();
    setConsoleOutput([]);
  };

  const handleSave = () => {
    const data = { html, css, js, timestamp: Date.now() };
    localStorage.setItem('freditor_autosave', JSON.stringify(data));
    if (onSave) onSave(data);
    
    // Visual feedback
    const btn = document.getElementById('save-btn');
    if (btn) {
      btn.style.background = '#48bb78';
      setTimeout(() => btn.style.background = '', 1000);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([generateOutput()], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'freditor-project.html';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    if (window.confirm('Bütün kodları ilkin vəziyyətə qaytarmaq istəyirsiniz?')) {
      setHtml(defaultHtml);
      setCss(defaultCss);
      setJs(defaultJs);
      setConsoleOutput([]);
      setErrors([]);
    }
  };

  const handleFormat = () => {
    // Simple formatting
    const formatCode = (code) => {
      let indent = 0;
      return code.split('\n').map(line => {
        line = line.trim();
        if (line.includes('}')) indent = Math.max(0, indent - 1);
        const formatted = '  '.repeat(indent) + line;
        if (line.includes('{')) indent++;
        return formatted;
      }).join('\n');
    };
    
    if (activeTab === 'html') setHtml(formatCode(html));
    if (activeTab === 'css') setCss(formatCode(css));
    if (activeTab === 'js') setJs(formatCode(js));
  };

  const insertTemplate = (type) => {
    const templates = {
      button: '<button class="btn">Click me</button>',
      form: `<form onsubmit="event.preventDefault()">
  <input type="text" placeholder="Adınız" class="input">
  <button type="submit">Göndər</button>
</form>`,
      fetch: `fetch('https://api.example.com/data')
  .then(r => r.json())
  .then(data => console.log(data))
  .catch(err => console.error(err));`,
      interval: `let count = 0;
const interval = setInterval(() => {
  console.log('Count:', ++count);
  if (count >= 5) clearInterval(interval);
}, 1000);`
    };
    
    const template = templates[type];
    if (!template) return;
    
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = textarea.value.substring(0, start) + template + textarea.value.substring(end);
      
      if (activeTab === 'html') setHtml(newValue);
      if (activeTab === 'css') setCss(newValue);
      if (activeTab === 'js') setJs(newValue);
    }
  };

  // ============================================
  // RENDER HELPERS
  // ============================================

  const getEditorValue = () => {
    if (activeTab === 'html') return html;
    if (activeTab === 'css') return css;
    return js;
  };

  const setEditorValue = (value) => {
    if (activeTab === 'html') setHtml(value);
    if (activeTab === 'css') setCss(value);
    if (activeTab === 'js') setJs(value);
  };

  const getLanguageIcon = () => {
    if (activeTab === 'html') return <FileType2 size={18} color="#e34c26" />;
    if (activeTab === 'css') return <FileJson size={18} color="#264de4" />;
    return <Braces size={18} color="#f7df1e" />;
  };

  const getDeviceWidth = () => {
    switch (device) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      default: return '100%';
    }
  };

  // ============================================
  // STYLES
  // ============================================

  const theme = {
    bg: darkMode ? '#1e1e1e' : '#ffffff',
    panel: darkMode ? '#252526' : '#f7fafc',
    border: darkMode ? '#333' : '#e2e8f0',
    text: darkMode ? '#d4d4d4' : '#2d3748',
    textSecondary: darkMode ? '#858585' : '#718096',
    accent: '#667eea',
    error: '#f56565',
    success: '#48bb78',
    warning: '#ed8936'
  };

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: isFullscreen ? '100vh' : height, 
      background: theme.bg,
      color: theme.text,
      fontFamily: "'Inter', sans-serif",
      overflow: 'hidden'
    }}>
      {/* HEADER */}
      <div style={{ 
        padding: '12px 20px', 
        background: theme.panel, 
        borderBottom: `1px solid ${theme.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '20px' }}>
          <Code2 size={24} color={theme.accent} />
          <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Freditor</span>
          <span style={{ 
            fontSize: '0.7rem', 
            background: theme.accent,
            color: 'white',
            padding: '2px 6px',
            borderRadius: '4px'
          }}>v2.0</span>
        </div>

        {/* Language Tabs */}
        <div style={{ display: 'flex', gap: '4px' }}>
          {[
            { id: 'html', label: 'HTML', color: '#e34c26' },
            { id: 'css', label: 'SCSS', color: '#264de4' },
            { id: 'js', label: 'JavaScript', color: '#f7df1e' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '8px 16px',
                background: activeTab === tab.id ? tab.color : 'transparent',
                color: activeTab === tab.id ? 'white' : theme.text,
                border: `1px solid ${activeTab === tab.id ? tab.color : theme.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              {tab.id === 'html' && <FileType2 size={16} />}
              {tab.id === 'css' && <FileJson size={16} />}
              {tab.id === 'js' && <Braces size={16} />}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Auto-run toggle */}
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            fontSize: '0.875rem',
            color: theme.textSecondary,
            cursor: 'pointer'
          }}>
            <input 
              type="checkbox" 
              checked={autoRun} 
              onChange={(e) => setAutoRun(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Auto-run
          </label>

          <button 
            onClick={handleManualRun}
            disabled={autoRun}
            style={{
              padding: '8px 16px',
              background: autoRun ? theme.border : theme.success,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: autoRun ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.875rem'
            }}
          >
            <Play size={16} />
            Run
          </button>

          <button 
            id="save-btn"
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              background: theme.accent,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.875rem'
            }}
          >
            <Save size={16} />
            Save
          </button>

          <button 
            onClick={handleDownload}
            style={{
              padding: '8px',
              background: theme.panel,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              cursor: 'pointer'
            }}
            title="Download HTML"
          >
            <Download size={16} />
          </button>

          <button 
            onClick={() => setShowSettings(!showSettings)}
            style={{
              padding: '8px',
              background: showSettings ? theme.accent : theme.panel,
              color: showSettings ? 'white' : theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            <Settings size={16} />
          </button>

          <button 
            onClick={() => setIsFullscreen(!isFullscreen)}
            style={{
              padding: '8px',
              background: theme.panel,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
      </div>

      {/* SETTINGS PANEL */}
      {showSettings && (
        <div style={{
          padding: '16px 20px',
          background: theme.panel,
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          gap: '24px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {/* Layout */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.75rem', color: theme.textSecondary, textTransform: 'uppercase' }}>
              Layout
            </label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[
                { id: 'horizontal', icon: Layout },
                { id: 'vertical', icon: Monitor },
                { id: 'preview-only', icon: Play },
                { id: 'editor-only', icon: Code2 }
              ].map((l) => (
                <button
                  key={l.id}
                  onClick={() => setLayout(l.id)}
                  style={{
                    padding: '6px',
                    background: layout === l.id ? theme.accent : 'transparent',
                    color: layout === l.id ? 'white' : theme.text,
                    border: `1px solid ${layout === l.id ? theme.accent : theme.border}`,
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  title={l.id}
                >
                  <l.icon size={16} />
                </button>
              ))}
            </div>
          </div>

          {/* Device */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.75rem', color: theme.textSecondary, textTransform: 'uppercase' }}>
              Device
            </label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[
                { id: 'desktop', icon: Monitor },
                { id: 'tablet', icon: Tablet },
                { id: 'mobile', icon: Smartphone }
              ].map((d) => (
                <button
                  key={d.id}
                  onClick={() => setDevice(d.id)}
                  style={{
                    padding: '6px',
                    background: device === d.id ? theme.accent : 'transparent',
                    color: device === d.id ? 'white' : theme.text,
                    border: `1px solid ${device === d.id ? theme.accent : theme.border}`,
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  title={d.id}
                >
                  <d.icon size={16} />
                </button>
              ))}
            </div>
          </div>

          {/* Theme */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.75rem', color: theme.textSecondary, textTransform: 'uppercase' }}>
              Theme
            </label>
            <button
              onClick={() => setDarkMode(!darkMode)}
              style={{
                padding: '6px 12px',
                background: theme.panel,
                color: theme.text,
                border: `1px solid ${theme.border}`,
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {darkMode ? <Moon size={16} /> : <Sun size={16} />}
              {darkMode ? 'Dark' : 'Light'}
            </button>
          </div>

          {/* Templates */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.75rem', color: theme.textSecondary, textTransform: 'uppercase' }}>
              Insert Template
            </label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['button', 'form', 'fetch', 'interval'].map((t) => (
                <button
                  key={t}
                  onClick={() => insertTemplate(t)}
                  style={{
                    padding: '4px 8px',
                    background: theme.panel,
                    color: theme.text,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    textTransform: 'capitalize'
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <button 
            onClick={handleReset}
            style={{
              marginLeft: 'auto',
              padding: '8px 16px',
              background: theme.error,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.875rem'
            }}
          >
            <RefreshCw size={16} />
            Reset
          </button>
        </div>
      )}

      {/* MAIN CONTENT */}
      <div style={{ 
        display: 'flex', 
        flex: 1,
        flexDirection: layout === 'vertical' ? 'column' : 'row',
        overflow: 'hidden'
      }}>
        {/* EDITOR PANEL */}
        {(layout !== 'preview-only') && (
          <div style={{ 
            flex: layout === 'editor-only' ? 1 : (layout === 'vertical' ? 1 : 1),
            display: 'flex',
            flexDirection: 'column',
            borderRight: layout !== 'vertical' ? `1px solid ${theme.border}` : 'none',
            borderBottom: layout === 'vertical' ? `1px solid ${theme.border}` : 'none',
            minWidth: layout === 'horizontal' ? '300px' : 'auto'
          }}>
            {/* Editor Toolbar */}
            <div style={{
              padding: '8px 12px',
              background: darkMode ? '#2d2d30' : '#edf2f7',
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              {getLanguageIcon()}
              <span style={{ 
                fontSize: '0.875rem', 
                fontWeight: 500,
                textTransform: 'uppercase'
              }}>
                {activeTab === 'html' ? 'HTML' : activeTab === 'css' ? 'SCSS/CSS' : 'JavaScript'}
              </span>
              
              <div style={{ flex: 1 }} />
              
              <button 
                onClick={handleFormat}
                style={{
                  padding: '4px 8px',
                  background: 'transparent',
                  color: theme.textSecondary,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.75rem'
                }}
              >
                Format
              </button>
              
              <span style={{ fontSize: '0.75rem', color: theme.textSecondary }}>
                {getEditorValue().length} chars
              </span>
            </div>

            {/* Editor Textarea */}
            <textarea
              ref={textareaRef}
              value={getEditorValue()}
              onChange={(e) => setEditorValue(e.target.value)}
              spellCheck="false"
              readOnly={readOnly}
              style={{
                flex: 1,
                width: '100%',
                padding: '16px',
                background: darkMode ? '#1e1e1e' : '#ffffff',
                color: theme.text,
                fontFamily: '"Fira Code", "Consolas", "Monaco", monospace',
                fontSize: '14px',
                lineHeight: '1.6',
                border: 'none',
                outline: 'none',
                resize: 'none',
                tabSize: 2
              }}
            />
          </div>
        )}

        {/* PREVIEW PANEL */}
        {(layout !== 'editor-only') && (
          <div style={{ 
            flex: layout === 'preview-only' ? 1 : (layout === 'vertical' ? 1 : 1),
            display: 'flex',
            flexDirection: 'column',
            background: '#f7fafc'
          }}>
            {/* Preview Toolbar */}
            <div style={{
              padding: '8px 12px',
              background: '#ffffff',
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Monitor size={16} color={theme.textSecondary} />
              <span style={{ fontSize: '0.875rem', color: theme.textSecondary }}>
                Preview {device !== 'desktop' && `(${device})`}
              </span>
              
              {isCompiling && (
                <span style={{ 
                  fontSize: '0.75rem', 
                  color: theme.accent,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <RefreshCw size={12} className="spin" />
                  Compiling...
                </span>
              )}
              
              <div style={{ flex: 1 }} />
              
              <button 
                onClick={() => setConsoleOutput([])}
                style={{
                  padding: '4px 8px',
                  background: 'transparent',
                  color: theme.textSecondary,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Terminal size={12} />
                Clear Console
              </button>
            </div>

            {/* Preview iframe */}
            <div style={{ 
              flex: 1, 
              position: 'relative',
              overflow: 'auto',
              display: 'flex',
              justifyContent: device !== 'desktop' ? 'center' : 'stretch',
              padding: device !== 'desktop' ? '20px' : '0'
            }}>
              <div style={{
                width: getDeviceWidth(),
                height: '100%',
                boxShadow: device !== 'desktop' ? '0 20px 60px rgba(0,0,0,0.2)' : 'none',
                borderRadius: device !== 'desktop' ? '12px' : '0',
                overflow: 'hidden',
                background: 'white'
              }}>
                <iframe
                  ref={iframeRef}
                  title="preview"
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    border: 'none',
                    background: 'white'
                  }}
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                />
              </div>
            </div>

            {/* CONSOLE PANEL */}
            <div style={{
              height: '150px',
              background: darkMode ? '#1e1e1e' : '#f7fafc',
              borderTop: `1px solid ${theme.border}`,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                padding: '6px 12px',
                background: darkMode ? '#252526' : '#edf2f7',
                borderBottom: `1px solid ${theme.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.75rem',
                color: theme.textSecondary,
                textTransform: 'uppercase'
              }}>
                <Terminal size={12} />
                Console ({consoleOutput.length})
              </div>
              
              <div style={{
                flex: 1,
                overflow: 'auto',
                padding: '8px 12px',
                fontFamily: '"Fira Code", monospace',
                fontSize: '12px'
              }}>
                {consoleOutput.length === 0 ? (
                  <span style={{ color: theme.textSecondary, fontStyle: 'italic' }}>
                    Console is empty. Use console.log() to see output here.
                  </span>
                ) : (
                  consoleOutput.map((log, idx) => (
                    <div 
                      key={idx}
                      style={{
                        marginBottom: '4px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: log.level === 'error' ? 'rgba(245, 101, 101, 0.1)' : 
                                    log.level === 'warn' ? 'rgba(237, 137, 54, 0.1)' : 'transparent',
                        color: log.level === 'error' ? theme.error : 
                               log.level === 'warn' ? theme.warning : theme.text,
                        borderLeft: `3px solid ${
                          log.level === 'error' ? theme.error : 
                          log.level === 'warn' ? theme.warning : theme.success
                        }`
                      }}
                    >
                      <span style={{ color: theme.textSecondary, marginRight: '8px' }}>
                        {log.timestamp}
                      </span>
                      <span>{log.data}</span>
                    </div>
                  ))
                )}
                <div ref={consoleEndRef} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ERRORS TOAST */}
      {errors.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          maxWidth: '400px',
          background: theme.error,
          color: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          zIndex: 1000
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <AlertCircle size={20} />
            <strong>Compilation Error</strong>
            <button 
              onClick={() => setErrors([])}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              <X size={16} />
            </button>
          </div>
          {errors.map((err, idx) => (
            <p key={idx} style={{ margin: '4px 0', fontSize: '0.875rem' }}>{err}</p>
          ))}
        </div>
      )}
    </div>
  );
};

// Lucide X icon component (since it's used but not imported)
const X = ({ size }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

export default Freditor;